import { useCallback, useMemo } from "react";

import { CheckIcon } from "@phosphor-icons/react";
import { match } from "ts-pattern";

import Button, { ButtonSize } from "@gx/dls/buttons/Button";
import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, {
  AlignItems,
  FlexDirection,
  JustifyContent,
} from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import CodeEditor from "@gx/dls/editor/CodeEditor";
import HelpIcon from "@gx/dls/icons/HelpIcon";
import RequiredMarker from "@gx/dls/text/RequiredMarker";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import Tooltip, { TooltipPosition } from "@gx/dls/tooltip/Tooltip";
import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";

import CreateSourceModalHeader from "@/pages/sources/components/create/components/CreateSourceModalHeader";
import CreateSourceModalWrapper from "@/pages/sources/components/create/components/CreateSourceModalWrapper";
import CreateSourceNameInput from "@/pages/sources/components/create/components/CreateSourceNameInput";
import { TextActionType } from "@/pages/sources/components/create/sources/text/actions";
import { SOURCE_TEXT_MAX_CHARACTERS_LENGTH } from "@/pages/sources/components/create/sources/text/constants";
import { useSourceText } from "@/pages/sources/components/create/sources/text/hooks";
import SourceTextProvider from "@/pages/sources/components/create/sources/text/SourceTextProvider";
import { TextPhase } from "@/pages/sources/components/create/sources/text/types";
import { CreateSourceStep } from "@/pages/sources/components/create/types";

import { NOOP } from "@/utils/helpers";

interface CreateSourceTextProps {
  onClose: () => void;
  onBack: () => void;
}

const CreateSourceTextContent = ({ onClose, onBack }: CreateSourceTextProps) => {
  const { state, dispatch, save, isDisabled, nameError, textContentError } = useSourceText({
    onSuccess: () => onClose(),
  });

  const handleSourceNameChange = useCallback(
    (name: string) => {
      dispatch({ type: TextActionType.SET_SOURCE_NAME, payload: name });
    },
    [dispatch],
  );

  const handleTextContentChange = useCallback(
    (content: string) => {
      dispatch({ type: TextActionType.SET_TEXT_CONTENT, payload: content });
    },
    [dispatch],
  );

  const renderButton = useMemo(() => {
    return match(state.phase)
      .with(TextPhase.IN_PROGRESS, () => (
        <Button
          label="Creating..."
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={true}
          isDisabled={true}
          isIconTrailing
          fillWidth
        />
      ))
      .with(TextPhase.COMPLETED, () => (
        <Button
          label="Done"
          icon={CheckIcon}
          size={ButtonSize.LARGE}
          onClick={NOOP}
          isLoading={false}
          isDisabled={true}
          isIconTrailing
          fillWidth
        />
      ))
      .otherwise(() => (
        <Button
          label="Save"
          size={ButtonSize.LARGE}
          onClick={() => void save()}
          isLoading={false}
          isDisabled={state.textContent.length === 0 || !!textContentError}
          fillWidth
        />
      ));
  }, [state.phase, state.textContent.length, save, textContentError]);

  return (
    <CreateSourceModalWrapper step={CreateSourceStep.CONFIGURE}>
      <CreateSourceModalHeader sourceType={SourceType.TEXT} onClose={onClose} onBack={onBack} />
      <FlexItem>
        <HorizontalDivider />
      </FlexItem>

      <FlexItem grow={1} minHeight={0} fillWidth overflow="auto" padding="16px">
        <FlexWrapper direction={FlexDirection.COLUMN} gap={16} fillWidth fillHeight>
          <CreateSourceNameInput
            value={state.sourceName}
            onChange={handleSourceNameChange}
            error={nameError}
            isDisabled={isDisabled}
          />

          <FlexWrapper direction={FlexDirection.COLUMN} gap={8} fillWidth>
            <FlexWrapper alignItems={AlignItems.CENTER} gap={4}>
              <Text variant={TextVariant.SECONDARY}>
                Content
                <RequiredMarker />
              </Text>
              <Tooltip body="Enter or paste some text" position={TooltipPosition.RIGHT}>
                <HelpIcon />
              </Tooltip>
            </FlexWrapper>
            <FlexItem height={300} fillWidth>
              <CodeEditor
                content={state.textContent}
                onChange={handleTextContentChange}
                placeholder="Paste or type your text content here..."
                padding="6px 4px"
                borderRadius={5}
                isReadOnly={isDisabled}
              />
            </FlexItem>
            <FlexWrapper
              alignItems={AlignItems.CENTER}
              justifyContent={JustifyContent.SPACE_BETWEEN}
              fillWidth
              gap={4}
            >
              <FlexItem>
                {textContentError && <Text variant={TextVariant.ERROR}>{textContentError}</Text>}
              </FlexItem>
              <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
                {state.textContent.length.toLocaleString()} /{" "}
                {SOURCE_TEXT_MAX_CHARACTERS_LENGTH.toLocaleString()} characters
              </Text>
            </FlexWrapper>
          </FlexWrapper>
        </FlexWrapper>
      </FlexItem>

      <FlexWrapper direction={FlexDirection.COLUMN} fillWidth>
        <HorizontalDivider />
        <FlexWrapper padding="16px" fillWidth>
          {renderButton}
        </FlexWrapper>
      </FlexWrapper>
    </CreateSourceModalWrapper>
  );
};

const CreateSourceText = (props: CreateSourceTextProps) => (
  <SourceTextProvider>
    <CreateSourceTextContent {...props} />
  </SourceTextProvider>
);

export default CreateSourceText;
